import { useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { CircleCheckIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import type { TransactionFormData } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { ReviewInvitationModal } from '@modules/visibility-reviews/components/ReviewInvitationModal';
import { useLocale } from '@shared/hooks/locale';

type SuccessStepProps = {
  onAddAnother: () => void;
};

export const SuccessStep = ({ onAddAnother }: SuccessStepProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { getValues } = useFormContext<TransactionFormData>();
  const [openInvitationModal, setOpenInvitationModal] = useState(false);

  const teamId = me?.default_team?.id;
  const selectedAgentId = getValues('details.seller_broker_id');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 4,
          height: '100%',
          minHeight: 400,
        }}
      >
        <CircleCheckIcon
          style={{ marginBottom: 16 }}
          size={80}
          color={'var(--mui-palette-success-main)'}
        />

        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 'bold',
          }}
        >
          {t('Transaction submitted')}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            maxWidth: 400,
            marginBottom: '16px',
          }}
        >
          {t(
            "We'll review your transaction before it appears on your profile.",
          )}
        </Typography>

        <Box sx={{ mt: 6, mb: 4, width: '100%', maxWidth: 400 }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
            }}
          >
            {t('Next steps')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              marginBottom: '16px',
            }}
          >
            {t('Ask your client for a review to verify this transaction.')}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setOpenInvitationModal(true)}
            disabled={!teamId}
          >
            {t('Get reviews')}
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={onAddAnother}
            sx={{ mt: 1 }}
          >
            {t('Add another transaction')}
          </Button>
        </Box>
      </Box>
      <ReviewInvitationModal
        open={openInvitationModal}
        onClose={() => setOpenInvitationModal(false)}
        initialAgentId={selectedAgentId}
      />
    </>
  );
};
